import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {useStableRandom} from '@github-ui/use-stable-random'
import {ForYouItemSkeleton} from './shared/ForYouItemFallbacks'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import styles from './shared/ForYouSubSection.module.css'
import {useDecideNextAction} from './deciders/use-decide-next-action'
import {useViewerRoles} from './shared/use-viewer-roles'
import {useReviewRequestedData} from './reviewer/use-review-requested-data'
import {clsx} from 'clsx'
import {useHeaderPageData} from '../../../page-data/loaders/use-header-page-data'
import {useDownstackPendingReviewRequests} from './stack/use-downstack-pending-review-request'
import {useMergeabilityData} from './mergeability/use-mergeability-data'

export function ForYouNextAction() {
  // Keep the phrase outside Suspense so both loading paths use the same React ID.
  const phraseIndex = useStableRandom({min: 0, max: LOADING_PHRASES.length - 1})
  const loadingFallback = <ForYouNextActionSkeleton text={LOADING_PHRASES[phraseIndex]} />

  return (
    <ErrorBoundary boundaryName="ForYouNextActionBoundary" fallback={null}>
      <Suspense fallback={loadingFallback}>
        <ForYouNextActionContent loadingFallback={loadingFallback} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ForYouNextActionContent({loadingFallback}: {loadingFallback: React.ReactNode}) {
  const {queryStatus: viewerRolesQueryStatus} = useViewerRoles()
  const {queryStatus: reviewRequestsQueryStatus} = useReviewRequestedData()
  const {pullRequest, stack} = useHeaderPageData()
  const {queryStatus: downstackPendingReviewRequestQueryStatus} = useDownstackPendingReviewRequests(
    stack,
    pullRequest.number,
  )
  const mergeabilityData = useMergeabilityData()
  const recommendedAction = useDecideNextAction()

  if (
    viewerRolesQueryStatus === 'loading' ||
    reviewRequestsQueryStatus === 'loading' ||
    downstackPendingReviewRequestQueryStatus === 'loading' ||
    (mergeabilityData?.stackSection.loadingEntryCount ?? 0) > 0
  ) {
    return loadingFallback
  }

  if (viewerRolesQueryStatus !== 'success' || reviewRequestsQueryStatus !== 'success' || recommendedAction === null) {
    return null
  }

  // since we're re-using the For You implementation, we need to wrap the list of items in a <ul>
  // so that the <li> elements inside ForYouItem are valid for a11y.
  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul role="list" className={clsx(styles.list, styles.CondensedForYouList)}>
      {recommendedAction}
    </ul>
  )
}

function ForYouNextActionSkeleton({text}: {text: string | undefined}) {
  return (
    <ul className={clsx(styles.list, styles.CondensedForYouList)}>
      <ForYouItemSkeleton
        text={text}
        variant="embedded"
        action={<LoadingSkeleton variant="rounded" width="120px" height="28px" />}
        emphasized
      />
    </ul>
  )
}

const LOADING_PHRASES = [
  "Deserializing your pull request's destiny…",
  'git blame-ing the delay…',
  'Traversing the commit graph…',
  'Reticulating CI pipelines…',
  'Garbage collecting stale opinions…',
  'Checking if the dog ate your approvals…',
  'Begging the automerge daemon for a straight answer…',
  'Asking git log --oneline for the meaning of life…',
  'Checking out your fate on a detached HEAD…',
]
